import { RealEstateSearch } from '../../models/real-estate-search.model';
import { restoreExecutedSearch, resetSearchHistorySession, searchViewportChanged } from '../../search-history/search-history.actions';
import { sharedModeFilters } from '../../search-history/search-history.model';
import { createReducer, on } from '@ngrx/store';
import { RealEstateSearchResult } from '../../models/real-estate.model';
import { mapSearchSnapshot, withoutViewport } from '../../models/search-request';
import { clearAuthUser, clearSessionState, logout } from '../actions/auth.actions';
import {
  addRealEstate,
  addRealEstateFailure,
  addRealEstateReportSuccess,
  addRealEstateSuccess,
  changeRealEstatePage,
  changeRealEstatePageSize,
  changeRealEstateSort,
  clearRealEstatePolygon,
  ensureRealEstateList,
  getRealEstateFailure,
  getRealEstateSuccess,
  getTopRealEstateFailure,
  getTopRealEstates,
  getTopRealEstatesSuccess,
  likeRealEstateFailure,
  likeRealEstateSuccess,
  removeRealEstateFailure,
  removeRealEstateSuccess,
  resetRealEstateSearch,
  retryRealEstateSearch,
  searchMapRealEstates,
  searchMapRealEstatesFailure,
  searchMapRealEstatesSuccess,
  searchRealEstateReports,
  searchRealEstateReportsFailure,
  searchRealEstateReportsSuccess,
  searchRealEstates,
  searchRealEstatesFailure,
  searchRealEstatesSuccess,
  setRealEstateMapView,
  setRealEstatePolygon,
  setRealEstateSoldFailure,
  setRealEstateSoldSuccess,
  toggleRealEstateMapView,
  updateRealEstate,
  updateRealEstateFailure,
  updateRealEstateReportFailure,
  updateRealEstateReportSuccess,
  updateRealEstateSuccess,
  updateSearchForm,
  upgradeRealEstate,
} from '../actions/real-estate.actions';
import { initialSearchState } from '../states/real-estate.state';

export const realEstateReducer = createReducer(
  initialSearchState,
  on(resetSearchHistorySession, state => ({ ...initialSearchState, searchRestoreRevision: state.searchRestoreRevision + 1 })),
  on(searchViewportChanged, (state, action) => action.domain !== 'REAL_ESTATE' ? state : ({ ...state, mapViewport: action.viewport })),
  on(restoreExecutedSearch, (state, entry) => {
    if (entry.domain !== 'REAL_ESTATE') return state;
    const criteria = entry.criteria as unknown as RealEstateSearch;
    const isMap = entry.mode === 'map';
    const shared = withoutViewport(criteria);
    return { ...state, historyRestored: true, searchRestoreRevision: state.searchRestoreRevision + 1,
      isRealEstateMapView: isMap, query: isMap ? state.query : shared,
      listDraft: structuredClone(shared), mapDraft: structuredClone(shared),
      mapQuery: isMap ? structuredClone(criteria) : state.mapQuery,
      activePolygon: isMap ? criteria.polygonFilter ?? null : state.activePolygon,
      polygonRevision: state.polygonRevision + 1,
      mapViewport: isMap ? entry.viewport : state.mapViewport,
      listLoading: !isMap, mapLoading: isMap, listError: null, mapError: null,
      propertyResults: isMap ? state.propertyResults : undefined, propertyMapResults: isMap ? undefined : state.propertyMapResults,
      sort: isMap ? state.sort : { active: criteria.sort ?? null, direction: criteria.direction ?? null },
      mapSort: isMap ? { active: criteria.sort ?? null, direction: criteria.direction ?? null } : state.mapSort,
      pagination: {...state.pagination, pageIndex: 0, ...(!isMap && criteria.limit && criteria.limit > 0 ? {pageSize: criteria.limit} : {})},
      mapPagination: {...state.mapPagination, pageIndex: 0},
    };
  }),


  on(logout, clearAuthUser, clearSessionState, (state) => ({
    ...initialSearchState,
    polygonRevision: state.polygonRevision + 1,
  })),
  on(resetRealEstateSearch, (state) => ({
    ...state,
    ...(state.isRealEstateMapView
      ? {
          mapQuery: undefined,
          mapDraft: undefined,
          propertyMapResults: undefined,
          activePolygon: null,
          polygonRevision: state.polygonRevision + 1,
          mapLoading: false,
          mapError: null,
          mapPagination: { pageIndex: 0, pageSize: 12 },
          mapSort: { active: null, direction: null },
        }
      : {
          query: undefined,
          listDraft: undefined,
          propertyResults: undefined,
          listLoading: false,
          listError: null,
          pagination: { pageIndex: 0, pageSize: 12 },
          sort: { active: null, direction: null },
        }),
  })),
  on(setRealEstatePolygon, (state, { polygon }) => ({
    ...state,
    activePolygon: polygon ? polygon.map((p) => ({ ...p })) : null,
    mapQuery:
      (state.mapDraft ?? state.mapQuery ?? state.query)
        ? structuredClone(withoutViewport((state.mapDraft ?? state.mapQuery ?? state.query)!))
        : undefined,
    mapPagination: { ...state.mapPagination, pageIndex: 0 },
    mapLoading: !!polygon,
    mapError: null,
    propertyMapResults: undefined,
  })),

  on(searchRealEstates, (state, { query }) => ({
    ...state,
    query: structuredClone(withoutViewport(query)),
    listDraft: structuredClone(withoutViewport(query)),
    pagination: { ...state.pagination, pageIndex: 0 },
    listLoading: true,
    listError: null,
    propertyResults: undefined,
  })),

  on(searchRealEstatesSuccess, (state, { propertyResults }) => ({
    ...state,
    propertyResults,
    listLoading: false,
    listError: null,
    pagination: {
      ...state.pagination,
      pageIndex: propertyResults?.pageIndex ?? state.pagination.pageIndex,
    },
  })),

  on(searchRealEstatesFailure, (state, { error }) => ({
    ...state,
    listLoading: false,
    listError: error,
  })),

  on(searchMapRealEstates, (state, { mapQuery }) => ({
    ...state,
    mapQuery: mapSearchSnapshot(mapQuery, state.mapQuery),
    mapDraft: structuredClone(withoutViewport(mapQuery)),
    mapPagination: { ...state.mapPagination, pageIndex: 0 },
    mapLoading: true,
    mapError: null,
    propertyMapResults: undefined,
  })),

  on(searchMapRealEstatesSuccess, (state, { propertyMapResults }) => ({
    ...state,
    propertyMapResults,
    mapLoading: false,
    mapError: null,
  })),

  on(searchMapRealEstatesFailure, (state, { error }) => ({
    ...state,
    mapLoading: false,
    mapError: error,
  })),

  on(changeRealEstateSort, (state, { active, direction }) => ({
    ...state,
    ...(state.isRealEstateMapView
      ? { mapSort: { active, direction }, mapPagination: { ...state.mapPagination, pageIndex: 0 } }
      : {
          sort: { active, direction },
          pagination: { ...state.pagination, pageIndex: 0 },
          listLoading: true,
          listError: null,
          propertyResults: undefined,
        }),
  })),

  on(changeRealEstatePage, (state, { pageIndex }) => ({
    ...state,
    pagination: {
      ...state.pagination,
      pageIndex,
    },
  })),

  on(changeRealEstatePageSize, (state, { pageSize }) => ({
    ...state,
    pagination: {
      pageSize,
      pageIndex: 0,
    },
  })),

  on(getTopRealEstates, (state) => ({
    ...state,
    topLoading: true,
    error: null,
  })),

  on(getTopRealEstatesSuccess, (state, { topPropertyResults: topPropertyResult }) => ({
    ...state,
    topPropertyResults: topPropertyResult,
    topLoading: false,
  })),

  on(getTopRealEstateFailure, (state, { error }) => ({
    ...state,
    topLoading: false,
    error,
  })),

  on(updateSearchForm, (state, { form }) => ({
    ...state,
    ...(state.isRealEstateMapView
      ? { mapDraft: structuredClone(withoutViewport(form)) }
      : { listDraft: structuredClone(withoutViewport(form)) }),
  })),

  on(getRealEstateSuccess, (state, { realEstate }) => ({
    ...state,
    loading: false,
    realEstate: realEstate,
  })),

  on(getRealEstateFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  on(addRealEstate, (state) => ({
    ...state,
    isSaving: true,
    error: null,
  })),

  on(addRealEstateSuccess, (state, { realEstate }) => ({
    ...state,
    isSaving: false,
    realEstate: realEstate,
    propertyResults: state.propertyResults
      ? { ...state.propertyResults, items: [...state.propertyResults.items, realEstate] }
      : ({
          items: [realEstate],
          total: 1,
          pageIndex: 0,
          pageSize: 12,
          pages: [1],
        } as RealEstateSearchResult),
  })),

  on(addRealEstateFailure, (state, { error }) => ({
    ...state,
    isSaving: false,
    error,
  })),

  on(updateRealEstate, (state) => ({
    ...state,
    isSaving: true,
    error: null,
  })),

  on(upgradeRealEstate, (state) => ({
    ...state,
    isSaving: true,
    error: null,
  })),

  on(updateRealEstateSuccess, (state, { realEstate }) => ({
    ...state,
    isSaving: false,
    realEstate: realEstate,
    propertyResults: state.propertyResults
      ? {
          ...state.propertyResults,
          items: state.propertyResults.items.map((item) =>
            item.id === realEstate.id ? realEstate : item
          ),
        }
      : state.propertyResults,

    topPropertyResults: state.topPropertyResults
      ? {
          ...state.topPropertyResults,
          items: state.topPropertyResults.items.map((item) =>
            item.id === realEstate.id ? realEstate : item
          ),
        }
      : state.topPropertyResults,

    propertyMapResults: state.propertyMapResults
      ? {
          ...state.propertyMapResults,
          items: state.propertyMapResults.items.map((item) =>
            item.id === realEstate.id ? realEstate : item
          ),
        }
      : state.propertyMapResults,
  })),

  on(updateRealEstateFailure, (state, { error }) => ({
    ...state,
    isSaving: false,
    error,
  })),

  on(likeRealEstateSuccess, (state, { realEstate }) => ({
    ...state,
    loading: false,
    realEstate,
    propertyResults: state.propertyResults
      ? {
          ...state.propertyResults,
          items: state.propertyResults.items.map((item) =>
            item.id === realEstate.id ? realEstate : item
          ),
        }
      : state.propertyResults,

    topPropertyResults: state.topPropertyResults
      ? {
          ...state.topPropertyResults,
          items: state.topPropertyResults.items.map((item) =>
            item.id === realEstate.id ? realEstate : item
          ),
        }
      : state.topPropertyResults,

    propertyMapResults: state.propertyMapResults
      ? {
          ...state.propertyMapResults,
          items: state.propertyMapResults.items.map((item) =>
            item.id === realEstate.id ? realEstate : item
          ),
        }
      : state.propertyMapResults,
  })),
  on(likeRealEstateFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  on(setRealEstateSoldSuccess, (state, { realEstate }) => ({
    ...state,
    isSaving: false,
    realEstate: realEstate,
    propertyResults: state.propertyResults
      ? {
          ...state.propertyResults,
          items: state.propertyResults.items.map((item) =>
            item.id === realEstate.id ? realEstate : item
          ),
        }
      : state.propertyResults,

    topPropertyResults: state.topPropertyResults
      ? {
          ...state.topPropertyResults,
          items: state.topPropertyResults.items.map((item) =>
            item.id === realEstate.id ? realEstate : item
          ),
        }
      : state.topPropertyResults,

    propertyMapResults: state.propertyMapResults
      ? {
          ...state.propertyMapResults,
          items: state.propertyMapResults.items.map((item) =>
            item.id === realEstate.id ? realEstate : item
          ),
        }
      : state.propertyMapResults,
  })),

  on(setRealEstateSoldFailure, (state, { error }) => ({
    ...state,
    isSaving: false,
    error,
  })),

  on(removeRealEstateSuccess, (state, { id }) => ({
    ...state,
    isSaving: false,
    realEstate: state.realEstate?.id === id ? undefined : state.realEstate,

    propertyResults: state.propertyResults
      ? {
          ...state.propertyResults,
          items: state.propertyResults.items.filter((item) => item.id !== id),
        }
      : state.propertyResults,

    topPropertyResults: state.topPropertyResults
      ? {
          ...state.topPropertyResults,
          items: state.topPropertyResults.items.filter((item) => item.id !== id),
        }
      : state.topPropertyResults,

    propertyMapResults: state.propertyMapResults
      ? {
          ...state.propertyMapResults,
          items: state.propertyMapResults.items.filter((item) => item.id !== id),
        }
      : state.propertyMapResults,
  })),

  on(removeRealEstateFailure, (state, { error }) => ({
    ...state,
    isSaving: false,
    error,
  })),

  on(searchRealEstateReports, (state, { reportQuery }) => ({
    ...state,
    reportQuery,
    loading: true,
    error: null,
  })),

  on(searchRealEstateReportsSuccess, (state, { realEstateReports: realEstateReports }) => ({
    ...state,
    propertyReports: realEstateReports,
    loading: false,
  })),

  on(searchRealEstateReportsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  on(addRealEstateReportSuccess, (state, { report }) => ({
    ...state,
    loading: false,
    propertyReports: state.propertyReports ? [...state.propertyReports, report] : [report],
  })),

  on(addRealEstateFailure, (state, { error }) => ({
    ...state,
    isSaving: false,
    error,
  })),

  on(updateRealEstateReportSuccess, (state, { report }) => ({
    ...state,
    loading: false,
    propertyReports: state.propertyReports?.map((c) => (c.id === report.id ? report : c)),
  })),

  on(updateRealEstateReportFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  on(setRealEstateMapView, (state, action) => {
    const isMap = action.isRealEstateMapView;
    if (isMap === state.isRealEstateMapView) return state;
    const { changed, filters } = sharedModeFilters(state, state.isRealEstateMapView, isMap);
    return { ...state, ...filters, isRealEstateMapView: isMap, historyRestored: false,
      propertyResults: !isMap && changed ? undefined : state.propertyResults,
      propertyMapResults: isMap && changed ? undefined : state.propertyMapResults,
      listLoading: !isMap && changed ? false : state.listLoading,
      mapLoading: isMap && changed ? false : state.mapLoading,
    };
  }),
  on(toggleRealEstateMapView, state => {
    const isMap = !state.isRealEstateMapView;
    const { changed, filters } = sharedModeFilters(state, state.isRealEstateMapView, isMap);
    return { ...state, ...filters, isRealEstateMapView: isMap, historyRestored: false,
      propertyResults: !isMap && changed ? undefined : state.propertyResults,
      propertyMapResults: isMap && changed ? undefined : state.propertyMapResults,
    };
  }),
  // Initial List loading must not invalidate explicit Map results.
  on(ensureRealEstateList, (state) =>
    state.historyRestored || state.isRealEstateMapView || state.propertyResults
      ? state
      : {
          ...state,
          query: state.query ?? structuredClone(state.listDraft),
          listLoading: true,
          listError: null,
        }
  ),
  on(clearRealEstatePolygon, (state) => ({
    ...state,
    activePolygon: null,
    polygonRevision: state.polygonRevision + 1,
    mapQuery: state.mapQuery ? withoutViewport(state.mapQuery) : undefined,
    propertyMapResults: undefined,
    mapLoading: false,
    mapError: null,
  })),
  on(changeRealEstatePage, changeRealEstatePageSize, (state) =>
    state.isRealEstateMapView
      ? state
      : { ...state, listLoading: true, listError: null, propertyResults: undefined }
  ),
  on(retryRealEstateSearch, (state) => ({
    ...state,
    ...(state.isRealEstateMapView
      ? { mapLoading: true, mapError: null, propertyMapResults: undefined }
      : { listLoading: true, listError: null, propertyResults: undefined }),
  }))
);
